import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Mail, Lock, User as UserIcon, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/components/AuthProvider";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";

export default function EmailAuthForm() {
  const [tab, setTab] = useState("login");
  const { t } = useTranslation();
  return (
    <div className="space-y-4" data-testid="email-auth-form">
      <div className="flex gap-1 p-1 rounded-lg bg-white/40 dark:bg-slate-900/30 border border-white/40 dark:border-white/10">
        <TabButton active={tab === "login"} onClick={() => setTab("login")} testId="auth-tab-login">
          {t("auth.tabs.login")}
        </TabButton>
        <TabButton active={tab === "register"} onClick={() => setTab("register")} testId="auth-tab-register">
          {t("auth.tabs.register")}
        </TabButton>
      </div>
      {tab === "login" && <LoginForm onForgot={() => setTab("forgot")} />}
      {tab === "register" && <RegisterForm />}
      {tab === "forgot" && <ForgotForm onBack={() => setTab("login")} />}
    </div>
  );
}

function TabButton({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`flex-1 text-sm py-2 rounded-md transition ${
        active
          ? "bg-white dark:bg-slate-800 text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function FieldIcon({ children }) {
  return <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">{children}</span>;
}

function LoginForm({ onForgot }) {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !pwd) return toast.error(t("auth.errors.fillAll"));
    setLoading(true);
    try {
      await api.post("/auth/login", { email: email.trim(), password: pwd });
      await refresh();
      toast.success(t("auth.welcomeBack"));
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.errors.generic"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="login-form">
      <div className="relative">
        <FieldIcon><Mail className="h-4 w-4" /></FieldIcon>
        <Input
          type="email" autoComplete="email" required
          value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder={t("auth.email")}
          className="pl-9 bg-white/70 dark:bg-slate-900/40"
          data-testid="login-email"
        />
      </div>
      <div className="relative">
        <FieldIcon><Lock className="h-4 w-4" /></FieldIcon>
        <Input
          type="password" autoComplete="current-password" required
          value={pwd} onChange={(e) => setPwd(e.target.value)}
          placeholder={t("auth.password")}
          className="pl-9 bg-white/70 dark:bg-slate-900/40"
          data-testid="login-password"
        />
      </div>
      <div className="flex items-center justify-end">
        <button type="button" onClick={onForgot} className="text-xs text-blue-600 dark:text-blue-300 hover:underline" data-testid="forgot-link">
          {t("auth.forgot")}
        </button>
      </div>
      <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="login-submit">
        {loading ? t("auth.loggingIn") : t("auth.login")}
      </Button>
    </form>
  );
}

function RegisterForm() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !pwd) return toast.error(t("auth.errors.fillAll"));
    if (pwd.length < 8) return toast.error(t("auth.errors.shortPassword"));
    setLoading(true);
    try {
      await api.post("/auth/register", { email: email.trim(), password: pwd, name: name.trim() || undefined });
      await refresh();
      toast.success(t("auth.accountCreated"));
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.errors.registerGeneric"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="register-form">
      <div className="relative">
        <FieldIcon><UserIcon className="h-4 w-4" /></FieldIcon>
        <Input
          autoComplete="name"
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder={t("auth.namePh")}
          className="pl-9 bg-white/70 dark:bg-slate-900/40"
          data-testid="register-name"
        />
      </div>
      <div className="relative">
        <FieldIcon><Mail className="h-4 w-4" /></FieldIcon>
        <Input
          type="email" autoComplete="email" required
          value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder={t("auth.email")}
          className="pl-9 bg-white/70 dark:bg-slate-900/40"
          data-testid="register-email"
        />
      </div>
      <div className="relative">
        <FieldIcon><Lock className="h-4 w-4" /></FieldIcon>
        <Input
          type="password" autoComplete="new-password" required
          value={pwd} onChange={(e) => setPwd(e.target.value)}
          placeholder={t("auth.passwordMin")}
          className="pl-9 bg-white/70 dark:bg-slate-900/40"
          data-testid="register-password"
        />
      </div>
      <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="register-submit">
        {loading ? t("auth.creating") : t("auth.register")}
      </Button>
      <p className="text-[11px] text-muted-foreground text-center">
        <Trans i18nKey="auth.terms" components={{ a: <a href="/terms" className="underline hover:text-foreground" /> }} />
      </p>
    </form>
  );
}

function ForgotForm({ onBack }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error(t("auth.errors.needEmail"));
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
      toast.success(t("auth.forgotSuccess"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error_generic"));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="space-y-3 text-center py-2" data-testid="forgot-sent">
        <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600">
          <Mail className="h-6 w-6" />
        </div>
        <p
          className="text-sm"
          dangerouslySetInnerHTML={{ __html: t("auth.forgotSentText", { email }) }}
        />
        <Button type="button" variant="ghost" onClick={onBack} className="text-blue-600" data-testid="forgot-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> {t("auth.returnLogin")}
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground -mt-1">
        <ArrowLeft className="h-3.5 w-3.5" /> {t("auth.back")}
      </button>
      <p className="text-sm text-muted-foreground">
        {t("auth.forgotDesc")}
      </p>
      <div className="relative">
        <FieldIcon><Mail className="h-4 w-4" /></FieldIcon>
        <Input
          type="email" autoComplete="email" required
          value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder={t("auth.email")}
          className="pl-9 bg-white/70 dark:bg-slate-900/40"
          data-testid="forgot-email"
        />
      </div>
      <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="forgot-submit">
        {loading ? t("auth.sending") : t("auth.sendLink")}
      </Button>
    </form>
  );
}
